import React from 'react'
import { getAttachmentFileUrl } from '../api/attachments'

export type InlineMediaKind = 'image' | 'video'

export type InlineMediaDraft = {
  token: string
  file: File
  kind: InlineMediaKind
  label: string
  placeholder: string
  previewUrl: string
}

const imageTokenPattern = /!\[([^\]]*)\]\(([^)]+)\)/g
const videoTokenPattern = /\[video:([^\]]*)\]\(([^)]+)\)/g
const pendingProtocol = 'pm-pending://'
const supportedInlineExtensions = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp'])

function sanitizeInlineLabel(label: string): string {
  const cleaned = label.replace(/[\[\]\r\n]+/g, ' ').trim()
  return cleaned || 'attachment'
}

export function buildInlineMediaMarkdown(kind: InlineMediaKind, label: string, url: string): string {
  const safeLabel = sanitizeInlineLabel(label)
  return kind === 'image'
    ? `![${safeLabel}](${url})`
    : `[video:${safeLabel}](${url})`
}

export function getPendingInlineMediaUrl(token: string): string {
  return `${pendingProtocol}${token}`
}

export function isInlineMediaFile(file: File): boolean {
  const extension = file.name.split('.').pop()?.toLowerCase() || ''
  return supportedInlineExtensions.has(extension)
}

export function getInlineMediaKind(file: File): InlineMediaKind | null {
  if (file.type.startsWith('image/')) return 'image'
  if (file.type === 'video/mp4') return 'video'

  const extension = file.name.split('.').pop()?.toLowerCase() || ''
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) return 'image'
  if (extension === 'mp4') return 'video'
  return null
}

function createInlineMediaToken(): string {
  return `media-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export function createInlineMediaDraft(file: File): InlineMediaDraft | null {
  const kind = getInlineMediaKind(file)
  if (!kind) return null

  const label = sanitizeInlineLabel(file.name)
  const token = createInlineMediaToken()

  return {
    token,
    file,
    kind,
    label,
    placeholder: buildInlineMediaMarkdown(kind, label, getPendingInlineMediaUrl(token)),
    previewUrl: URL.createObjectURL(file),
  }
}

export function insertInlineMediaPlaceholder(
  currentValue: string,
  selectionStart: number,
  selectionEnd: number,
  placeholder: string
): { nextValue: string; nextCaret: number } {
  const before = currentValue.slice(0, selectionStart)
  const after = currentValue.slice(selectionEnd)
  const needsLeadingBreak = before.length > 0 && !before.endsWith('\n')
  const needsTrailingBreak = after.length > 0 && !after.startsWith('\n')
  const insertion = `${needsLeadingBreak ? '\n' : ''}${placeholder}${needsTrailingBreak ? '\n' : ''}`
  const nextValue = `${before}${insertion}${after}`
  const nextCaret = before.length + insertion.length

  return { nextValue, nextCaret }
}

export function filterReferencedInlineMedia(description: string, drafts: InlineMediaDraft[]): InlineMediaDraft[] {
  return drafts.filter((draft) =>
    description.includes(draft.placeholder)
    || description.includes(getPendingInlineMediaUrl(draft.token))
    || description.includes(`data-inline-token="${draft.token}"`)
  )
}

export function stripPendingInlineMedia(description?: string): string | undefined {
  if (!description) return description

  const nextDescription = description
    .replace(imageTokenPattern, (match, label, url) => (String(url).startsWith(pendingProtocol) ? '' : match))
    .replace(videoTokenPattern, (match, label, url) => (String(url).startsWith(pendingProtocol) ? '' : match))
    .replace(/\n{3,}/g, '\n\n')
    .trim()

  return nextDescription || undefined
}

export function replaceInlineMediaPlaceholders(
  description: string | undefined,
  replacements: Array<{ draft: InlineMediaDraft; fileUrl: string; filename?: string }>
): string | undefined {
  let nextDescription = description || ''

  if (isProbablyHtml(nextDescription) && typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
    const parser = new DOMParser()
    const doc = parser.parseFromString(`<div>${nextDescription}</div>`, 'text/html')
    const root = doc.body.firstElementChild

    if (root) {
      for (const { draft, fileUrl, filename } of replacements) {
        root.querySelectorAll(`img[data-inline-token="${draft.token}"]`).forEach((node) => {
          if (!(node instanceof HTMLImageElement)) return
          node.setAttribute('src', fileUrl)
          node.setAttribute('alt', filename || draft.label)
          node.removeAttribute('data-inline-token')
        })
      }

      nextDescription = root.innerHTML
    }
  }

  for (const { draft, fileUrl, filename } of replacements) {
    const finalMarkdown = buildInlineMediaMarkdown(draft.kind, filename || draft.label, fileUrl)
    nextDescription = nextDescription.split(draft.placeholder).join(finalMarkdown)
    nextDescription = nextDescription.split(getPendingInlineMediaUrl(draft.token)).join(fileUrl)
    nextDescription = nextDescription.split(draft.previewUrl).join(fileUrl)
  }

  return nextDescription || undefined
}

type RichSegment =
  | { type: 'text'; value: string }
  | { type: 'image'; label: string; url: string; pending: boolean }
  | { type: 'video'; label: string; url: string; pending: boolean }
  | { type: 'link'; label: string; url: string }

function pushTextSegment(segments: RichSegment[], value: string) {
  if (!value) return
  segments.push({ type: 'text', value })
}

function parseIssueDescription(description: string): RichSegment[] {
  const segments: RichSegment[] = []
  const combinedPattern = /!\[([^\]]*)\]\(([^)]+)\)|\[video:([^\]]*)\]\(([^)]+)\)|\[([^\]]+)\]\(([^)]+)\)/g
  let cursor = 0

  description.replace(combinedPattern, (match, imageLabel, imageUrl, videoLabel, videoUrl, linkLabel, linkUrl, offset) => {
    pushTextSegment(segments, description.slice(cursor, offset))

    if (typeof imageUrl === 'string') {
      segments.push({
        type: 'image',
        label: sanitizeInlineLabel(imageLabel || 'image'),
        url: imageUrl,
        pending: imageUrl.startsWith(pendingProtocol),
      })
    } else if (typeof videoUrl === 'string') {
      segments.push({
        type: 'video',
        label: sanitizeInlineLabel(videoLabel || 'video'),
        url: videoUrl,
        pending: videoUrl.startsWith(pendingProtocol),
      })
    } else if (typeof linkUrl === 'string') {
      segments.push({
        type: 'link',
        label: sanitizeInlineLabel(linkLabel || linkUrl),
        url: linkUrl,
      })
    }

    cursor = offset + match.length
    return match
  })

  pushTextSegment(segments, description.slice(cursor))
  return segments
}

function renderTextWithBreaks(value: string) {
  const lines = value.split('\n')
  return lines.map((line, index) => (
    <React.Fragment key={`line-${index}`}>
      {line}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ))
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function isProbablyHtml(description: string) {
  return /<\/?[a-z][\s\S]*>/i.test(description)
}

function sanitizeUrl(url: string) {
  const trimmed = url.trim()
  if (
    trimmed.startsWith('http://')
    || trimmed.startsWith('https://')
    || trimmed.startsWith('mailto:')
    || trimmed.startsWith('tel:')
    || trimmed.startsWith(pendingProtocol)
    || trimmed.startsWith('/')
  ) {
    return trimmed
  }
  return ''
}

function getImageWidthStyle(value: string | null) {
  const parsed = Number(value || '')
  if (!Number.isFinite(parsed) || parsed < 120) return 'max-width:100%;height:auto;display:block;margin:10px 0;border-radius:12px;border:1px solid #dbe3ee;background:#f8fafc;'
  return `width:${parsed}px;max-width:100%;height:auto;display:block;margin:10px 0;border-radius:12px;border:1px solid #dbe3ee;background:#f8fafc;`
}

function sanitizeDescriptionHtml(description: string) {
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return escapeHtml(description)
  }

  const parser = new DOMParser()
  const doc = parser.parseFromString(`<div>${description}</div>`, 'text/html')
  const root = doc.body.firstElementChild
  if (!root) return ''

  const serializeNode = (node: ChildNode): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return escapeHtml(node.textContent || '')
    }

    if (!(node instanceof HTMLElement)) return ''

    const children = Array.from(node.childNodes).map(serializeNode).join('')
    const tag = node.tagName.toLowerCase()

    switch (tag) {
      case 'p':
      case 'ul':
      case 'ol':
      case 'li':
      case 'blockquote':
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'strong':
      case 'b':
      case 'em':
      case 'i':
      case 'u':
        return `<${tag}>${children}</${tag}>`
      case 'div':
        return `<div>${children}</div>`
      case 'br':
        return '<br />'
      case 'a': {
        const href = sanitizeUrl(node.getAttribute('href') || '')
        if (!href) return children
        return `<a href="${escapeHtml(href)}" target="_blank" rel="noreferrer">${children || escapeHtml(href)}</a>`
      }
      case 'img': {
        const src = sanitizeUrl(node.getAttribute('src') || '')
        if (!src) return ''
        const alt = escapeHtml(node.getAttribute('alt') || 'image')
        const width = node.getAttribute('data-width')
        const widthAttr = width && /^\d+$/.test(width) ? ` data-width="${width}"` : ''
        return `<img src="${escapeHtml(src)}" alt="${alt}"${widthAttr} style="${getImageWidthStyle(width)}" />`
      }
      default:
        return children
    }
  }

  return Array.from(root.childNodes).map(serializeNode).join('')
}

export function descriptionToEditableHtml(description?: string) {
  if (!description) return '<p><br></p>'
  if (isProbablyHtml(description)) return sanitizeDescriptionHtml(description)

  const tokenPattern = /!\[([^\]]*)\]\(([^)]+)\)|\[([^\]]+)\]\(([^)]+)\)/g
  const tokens: Array<{ key: string; html: string }> = []
  let tokenIndex = 0

  const withTokens = description.replace(tokenPattern, (match, imageLabel, imageUrl, linkLabel, linkUrl) => {
    const key = `__PM_TOKEN_${tokenIndex++}__`
    if (typeof imageUrl === 'string') {
      const src = sanitizeUrl(imageUrl)
      tokens.push({
        key,
        html: src ? `<img src="${escapeHtml(src)}" alt="${escapeHtml(imageLabel || 'image')}" style="${getImageWidthStyle(null)}" />` : '',
      })
    } else if (typeof linkUrl === 'string') {
      const href = sanitizeUrl(linkUrl)
      tokens.push({
        key,
        html: href ? `<a href="${escapeHtml(href)}" target="_blank" rel="noreferrer">${escapeHtml(linkLabel || href)}</a>` : escapeHtml(match),
      })
    }
    return key
  })

  const paragraphs = withTokens
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br />')}</p>`)

  let html = paragraphs.join('')
  for (const token of tokens) {
    html = html.split(token.key).join(token.html)
  }

  return html || '<p><br></p>'
}

function PendingInlineMedia({ label, kind }: { label: string; kind: InlineMediaKind }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        margin: '6px 0',
        padding: '8px 12px',
        borderRadius: 10,
        background: '#eef2ff',
        border: '1px dashed #a5b4fc',
        color: '#4338ca',
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      {kind === 'image' ? 'Image' : 'MP4'}
      <span style={{ fontWeight: 500 }}>{label}</span>
      <span style={{ color: '#6366f1' }}>will upload when you save</span>
    </span>
  )
}

function RenderInlineMedia({ segment }: { segment: Extract<RichSegment, { type: 'image' | 'video' }> }) {
  if (segment.pending) {
    return <PendingInlineMedia label={segment.label} kind={segment.type} />
  }

  const src = getAttachmentFileUrl(segment.url)

  if (segment.type === 'image') {
    return (
      <img
        src={src}
        alt={segment.label}
        style={{
          display: 'block',
          maxWidth: '100%',
          width: 'auto',
          maxHeight: 420,
          borderRadius: 12,
          margin: '10px 0',
          border: '1px solid #dbe3ee',
          background: '#f8fafc',
        }}
      />
    )
  }

  return (
    <video
      src={src}
      controls
      preload="metadata"
      style={{
        display: 'block',
        maxWidth: '100%',
        width: '100%',
        maxHeight: 420,
        borderRadius: 12,
        margin: '10px 0',
        border: '1px solid #dbe3ee',
        background: '#0f172a',
      }}
    />
  )
}

function RenderLink({ segment }: { segment: Extract<RichSegment, { type: 'link' }> }) {
  return (
    <a
      href={segment.url}
      target="_blank"
      rel="noreferrer"
      style={{
        color: '#2563eb',
        textDecoration: 'underline',
        wordBreak: 'break-word',
      }}
    >
      {segment.label}
    </a>
  )
}

export function IssueDescriptionContent({
  description,
  style,
}: {
  description?: string
  style?: React.CSSProperties
}) {
  if (!description) return null

  if (isProbablyHtml(description)) {
    return <div style={style} dangerouslySetInnerHTML={{ __html: sanitizeDescriptionHtml(description) }} />
  }

  const segments = parseIssueDescription(description)

  return (
    <div style={style}>
      {segments.map((segment, index) => {
        if (segment.type === 'text') {
          return <React.Fragment key={`text-${index}`}>{renderTextWithBreaks(segment.value)}</React.Fragment>
        }

        if (segment.type === 'link') {
          return <RenderLink key={`link-${index}`} segment={segment} />
        }

        return <RenderInlineMedia key={`${segment.type}-${index}`} segment={segment} />
      })}
    </div>
  )
}
